import React, { useEffect, useState } from 'react';
import DiffViewer from './DiffViewer';
import { compareVersions } from '../api';
import { X, RefreshCw } from 'lucide-react';

const VersionDiffModal = ({ contractId, oldVersion, newVersion, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [comparisonData, setComparisonData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchDiff = async () => {
            if (!oldVersion || !newVersion) return;
            setLoading(true);
            try {
                const data = await compareVersions(contractId, oldVersion.version_number, newVersion.version_number);
                setComparisonData(data);
            } catch (err) {
                console.error("Failed to fetch version diff", err);
                setError("Failed to load comparison.");
            } finally {
                setLoading(false);
            }
        };

        fetchDiff();
    }, [contractId, oldVersion, newVersion]);

    if (!oldVersion || !newVersion) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-[#1e1e1e] rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden animate-fade-in">
                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b border-[#3c3c3c] bg-[#252526]">
                    <div>
                        <h2 className="text-lg font-bold text-white flex items-center gap-2">
                            Version Comparison
                            <span className="text-xs font-normal bg-[#3c3c3c] px-2 py-0.5 rounded text-gray-500">
                                v{oldVersion.version_number} → v{newVersion.version_number}
                            </span>
                        </h2>
                        <p className="text-xs text-gray-400 mt-0.5">
                            Comparing {oldVersion.created_by || 'system'}'s version with {newVersion.created_by || 'system'}'s.
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-[#3c3c3c] rounded-full transition-colors text-gray-400">
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden p-0 relative">
                    {loading ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 bg-[#1e1e1e]">
                            <RefreshCw size={32} className="animate-spin mb-3 text-blue-500" />
                            <p>Calculating diff...</p>
                        </div>
                    ) : error ? (
                        <div className="absolute inset-0 flex items-center justify-center text-red-500 p-8 text-center">
                            {error}
                        </div>
                    ) : (
                        <div className="h-full flex flex-col">
                            <div className="flex-1 overflow-hidden">
                                <DiffViewer
                                    source={comparisonData?.source?.schema}
                                    target={comparisonData?.target?.schema}
                                    sourceName={`Version ${oldVersion.version_number}`}
                                    targetName={`Version ${newVersion.version_number}`}
                                    // Pass semantic hints if available from backend diff
                                    suggestedRenames={comparisonData?.diff?.suggested_renames || []}
                                    typeChanges={comparisonData?.diff?.type_changes || []}
                                />
                            </div>

                            {/* Summary Footer */}
                            {(newVersion.change_summary || comparisonData?.diff) && (
                                <div className="p-3 bg-[#252526] border-t border-[#3c3c3c] text-sm text-gray-300">
                                    <span className="font-semibold">Change Summary: </span>
                                    {newVersion.change_summary || "No description."}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VersionDiffModal;
